import { useState } from "react";
import { Key, CheckCircle, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { repApi, getRepSession } from "@/lib/repApi";

export default function RepAccount() {
  const session = getRepSession();
  const rep = session?.rep || {};
  const repInfo = rep.representative || {};
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("A nova senha precisa ter ao menos 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }
    setLoading(true);
    try {
      await repApi.post("/rep/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Senha alterada com sucesso.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao alterar a senha.");
    } finally {
      setLoading(false);
    }
  };

  const publicLink = repInfo.slug
    ? `${window.location.origin}/r/${repInfo.slug}`
    : null;

  const copyLink = () => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink);
    toast.success("Link copiado.");
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[760px] mx-auto">
      <header className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
          Portal do Representante
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
          Minha conta
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          Dados do seu perfil, link público de cadastro e gestão de senha.
        </p>
      </header>

      {/* Perfil */}
      <section className="bg-white/[0.03] border border-white/10 rounded-lg p-6 mb-5">
        <h2 className="font-heading text-[13px] font-semibold tracking-tight mb-4">
          Perfil
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Nome</dt>
            <dd className="text-white mt-1" data-testid="rep-account-name">{rep.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Email</dt>
            <dd className="text-white mt-1" data-testid="rep-account-email">{rep.email || "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Cidade</dt>
            <dd className="text-white mt-1">{repInfo.city || "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">UF</dt>
            <dd className="text-white mt-1">{repInfo.state || "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Comissão padrão</dt>
            <dd className="text-emerald-400 mt-1 tabular-nums">
              {((repInfo.default_commission_pct || 0) * 100).toFixed(0)}%
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Status</dt>
            <dd className="text-white mt-1 capitalize">{repInfo.status || "—"}</dd>
          </div>
        </dl>
        <p className="text-[11px] text-slate-500 mt-5 leading-relaxed">
          Para alterar nome, email, comissão ou região, fale com a equipe AgentUS.
        </p>
      </section>

      {/* Link de afiliado */}
      {publicLink && (
        <section
          className="bg-white/[0.03] border border-white/10 rounded-lg p-6 mb-5"
          data-testid="rep-account-link"
        >
          <h2 className="font-heading text-[13px] font-semibold tracking-tight mb-3 flex items-center gap-2">
            <LinkSimple size={14} weight="regular" /> Link público de cadastro
          </h2>
          <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-md px-3 py-2.5">
            <code className="flex-1 text-[12px] text-emerald-300 font-mono truncate">
              {publicLink}
            </code>
            <button
              onClick={copyLink}
              data-testid="rep-copy-link"
              className="text-[11px] text-slate-300 hover:text-white border border-white/10 hover:border-white/30 rounded-md px-2.5 py-1 transition-colors"
            >
              Copiar
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Qualquer cliente que se cadastrar pela AgentUS através deste link
            (ou de <code>?rep={repInfo.slug}</code>) é vinculado automaticamente à sua carteira.
          </p>
        </section>
      )}

      {/* Trocar senha */}
      <section className="bg-white/[0.03] border border-white/10 rounded-lg p-6">
        <h2 className="font-heading text-[13px] font-semibold tracking-tight mb-4 flex items-center gap-2">
          <Key size={14} weight="regular" /> Trocar senha
        </h2>
        <form onSubmit={submit} className="space-y-4" data-testid="rep-change-password-form">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2">
              Senha atual
            </span>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="rep-current-password"
              className="w-full px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30 transition-colors"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2">
                Nova senha
              </span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="rep-new-password"
                className="w-full px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30 transition-colors"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2">
                Confirmar nova senha
              </span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                data-testid="rep-confirm-password"
                className="w-full px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30 transition-colors"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="rep-change-password-submit"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 transition-colors disabled:opacity-60"
          >
            {loading ? "Atualizando…" : "Atualizar senha"}
            {!loading && <CheckCircle size={14} weight="regular" />}
          </button>
        </form>
      </section>
    </div>
  );
}
